export type StepKind =
'identification' |
'projectTypes' |
'studyPeriod' |
'text' |
'gantt' |
'grant' |
'principalInvestigator' |
'coInvestigators' |
'endorsements' |
'documents' |
'declaration';

export interface StepTextarea {
  key: string;
  source: 'narrative' | 'field';
  label: string;
  hint?: string;
  points?: string[];
  rows?: number;
  optional?: boolean;
}

export interface WizardStep {
  id: string;
  label: string;
  phase: number;
  kind: StepKind;
  /** What the researcher has to do here — shown in the timeline and step header. */
  summary: string;
  /** Nothing on this step was compulsory in the WebForms wizard. */
  optional?: boolean;
  textareas?: StepTextarea[];
}

/**
 * The three pages of the WebForms wizard — ResProposal.aspx, ResSubForm.aspx
 * and ResConsentForm.aspx — in their order and under their titles. The last
 * step submits, as ResConsentForm.aspx did.
 */
export const PHASES = [
{ n: 1, label: 'Research Proposal' },
{ n: 2, label: 'Research Proposal Submission' },
{ n: 3, label: 'Research Participant Consent' }];


const n = (
key: string,
label: string,
hint: string,
extra: Partial<StepTextarea> = {})
: StepTextarea => ({ key, source: 'narrative', label, hint, ...extra });

/*
 * Labels, guidance and what is compulsory follow ResProposal.aspx and
 * ResSubForm.aspx. Study type, sampling technique, review category, project
 * types, the time frame, co-investigators, HOD and supervisor were never
 * validated there, so they are optional here too.
 */
export const WIZARD_STEPS: WizardStep[] = [
{
  id: 'identification',
  label: 'Proposal Identification',
  phase: 1,
  kind: 'identification',
  summary: 'Title of proposal and the research proposal review category.'
},
{
  id: 'project-type',
  label: 'Type of Research Project',
  phase: 1,
  kind: 'projectTypes',
  optional: true,
  summary: 'Please check all applicable options.'
},
{
  id: 'study-period',
  label: 'Study Period',
  phase: 1,
  kind: 'studyPeriod',
  summary: 'Proposed starting date and duration of the research project (up to 24 months).'
},
{
  id: 'introduction',
  label: 'Introduction',
  phase: 1,
  kind: 'text',
  summary: 'Background, preliminary literature review and significance of the study.',
  textareas: [
  n(
    'introduction',
    'Introduction',
    '(Background, Preliminary Literature Review and Significance of the Study): One paragraph related to global work on the subject, one paragraph related to local (Pakistan) work and one paragraph related to institutional work with a total of 10 to 20 references. Last paragraph should indicate the importance of work and what information/gaps are missing on topic/subject and how this study will contribute to knowledge.',
    { rows: 7 }
  )]

},
{
  id: 'rationale',
  label: 'Rationale',
  phase: 1,
  kind: 'text',
  summary: 'Importance and purpose of the work.',
  textareas: [
  n(
    'rationale',
    'Rationale',
    '(Importance and purpose of the work): how this research will contribute to the elimination of a gap in the literature.',
    { rows: 6 }
  )]

},
{
  id: 'objectives',
  label: 'Objectives',
  phase: 1,
  kind: 'text',
  summary: 'Research questions with measurable outcomes.',
  textareas: [
  n(
    'objectives',
    'Objectives',
    'Formulate your research questions (Objectives) with measurable outcome like estimate, determine, calculate, correlate, outline, create or organize, that leads to achievement of goal.',
    { rows: 6 }
  )]

},
{
  id: 'design',
  label: 'Study Type, Setting, Duration',
  phase: 1,
  kind: 'text',
  optional: true,
  summary: 'Methodology: study type, setting and duration.',
  textareas: [
  n(
    'studyTypeSetting',
    'Study Type, Setting, Duration',
    'The study type should detail whether it is, retrospective, prospective, cross-sectional, interventional, comparative, experimental etc. Setting should indicate where the study will be conducted and for how long.',
    { rows: 5, optional: true }
  )]

},
{
  id: 'sample-size',
  label: 'Sample Size / Statistical Power',
  phase: 1,
  kind: 'text',
  summary: 'Sample size calculation, formula and basis of calculation.',
  textareas: [
  n(
    'sampleSize',
    'Sample size / Statistical Power',
    'Details of sample size calculation, formula, basis of calculation.',
    { rows: 5 }
  )]

},
{
  id: 'sampling',
  label: 'Sampling Technique',
  phase: 1,
  kind: 'text',
  optional: true,
  summary: 'How a representative sample will be obtained.',
  textareas: [
  n(
    'samplingTechnique',
    'Sampling Technique',
    '(simple random, randomization, probability, convenient etc, in order to obtain a representative sample for your target population)',
    { rows: 4, optional: true }
  )]

},
{
  id: 'population',
  label: 'Study Population',
  phase: 1,
  kind: 'text',
  summary: 'Human subjects under study.',
  textareas: [
  n(
    'studyPopulation',
    'Study Population',
    '(Human subjects under study) i. Describe the subjects: Include the total number to be recruited. If the study includes members of a vulnerable population or targets a particular religious, racial, ethnic or sexual-orientation population, then state a justification for the choice of subject population.',
    { rows: 4 }
  )]

},
{
  id: 'criteria',
  label: 'Inclusion & Exclusion Criteria',
  phase: 1,
  kind: 'text',
  summary: 'Who can and cannot be enrolled.',
  textareas: [
  n(
    'inclusion',
    'Inclusion Criteria',
    'Inclusion criteria should clearly detail the subjects to be included in study.',
    { rows: 4 }
  ),
  n(
    'exclusion',
    'Exclusion Criteria',
    'Exclusion criteria should include reason for exclusion within the inclusion group. Include flow charts where relevant.',
    { rows: 4 }
  )]

},
{
  id: 'instrument',
  label: 'Study Instrument',
  phase: 1,
  kind: 'text',
  summary: 'Proforma, questionnaire, interview, focus group discussion etc.',
  textareas: [
  n(
    'instrument',
    'Study Instrument',
    '(Proforma, Questionnaire, interview, focus group discussion etc)',
    { rows: 4 }
  )]

},
{
  id: 'procedure',
  label: 'Procedure / Methods',
  phase: 1,
  kind: 'text',
  summary: 'All the procedures and steps that will be undertaken, points i–vii.',
  textareas: [
  n(
    'procedure',
    'Procedure / methods in details',
    'Clearly detail all the procedures/steps that will be under taken.',
    {
      rows: 9,
      points: [
      'i. Describe the recruitment process.',
      'ii. What steps will you take to assure that participation is voluntary?',
      'iii. What will the subjects do and/or how will you interact with the subjects? Include the estimated duration of subject participation.',
      'iv. List the location(s) where data collection will be conducted.',
      'v. Laboratory and Radiological studies: in case research subjects are also patients, will any additional study related tests be performed which are not routinely required as part of the work up for the patient? Who will pay for these additional tests?',
      'vi. List ALL foreseeable risk to participants, including fatigue.',
      'vii. Data collection procedure; describe the instruments used for data collection (questionnaire, observation recording form, etc.), and the variables included in these instruments, as well as the methods used to test for the validity and reliability of the instrument, copy of drug brochure. Techniques used should be briefly described and referenced. Study definitions (e.g., case definition) should be mentioned. What are the variables of most interest? Who will collect the data? How will the data be recorded?']

    }
  )]

},
{
  id: 'data-analysis',
  label: 'Data Analysis Procedure',
  phase: 1,
  kind: 'text',
  summary: 'Analysis plan, tests and statistical packages.',
  textareas: [
  n(
    'dataAnalysis',
    'Data analysis procedure',
    '(Describe the analysis plan, tests used for data analysis and statistical package(s) used)',
    { rows: 5 }
  )]

},
{
  id: 'time-frame',
  label: 'Month-wise Time Frame',
  phase: 1,
  kind: 'gantt',
  optional: true,
  summary: 'Month wise frame of proposed activities as Gantt chart. Overlap is expected.'
},
{
  id: 'references',
  label: 'References',
  phase: 1,
  kind: 'text',
  summary: 'Not more than 10, in Vancouver style.',
  textareas: [
  {
    key: 'references',
    source: 'field',
    label: 'References',
    hint: 'Not more than 10 references with at least 50 percent should be of last 5 years. References should be given in Vancouver style (where references quoted as number in the text. The same number is reciprocated at back in the reference list).',
    rows: 8
  }]

},
{
  id: 'facilities',
  label: 'Institutional Facilities',
  phase: 1,
  kind: 'text',
  summary: 'Institutional facilities for carrying out the proposed study.',
  textareas: [
  {
    key: 'facilities',
    source: 'field',
    label: 'Institutional facilities for carrying out the proposed study',
    hint: 'Mention the facilities available in the institute which is related to the project and also the facilities which will be shared with other institutes. In case the biological material is shared with other institutes, full detail for handling and sharing, who will be the custodian of those, for how long and how those will be destroyed.',
    rows: 5
  }]

},
{
  id: 'benefits',
  label: 'Potential Benefits',
  phase: 1,
  kind: 'text',
  summary: 'Benefits to participants, society, sponsors and institutions.',
  textareas: [
  {
    key: 'potentialBenefits',
    source: 'field',
    label: 'Potential Benefits',
    hint: 'What are the actual benefits if any to be obtained by (i) participants, (ii) society as a result of this study, (iii) specific benefits to funding agency or sponsors and to the institutions where the study will be conducted?',
    rows: 5
  }]

},
{
  id: 'risks',
  label: 'Potential Risks',
  phase: 1,
  kind: 'text',
  summary: 'Risks involved for the research participants.',
  textareas: [
  {
    key: 'potentialRisks',
    source: 'field',
    label: 'Potential Risks',
    hint: 'What are short term & long term, minor, moderate and major risks involved for the research participants.',
    rows: 5
  }]

},
{
  id: 'ethics',
  label: 'Ethical Considerations',
  phase: 1,
  kind: 'text',
  summary: 'Ethical considerations that will be undertaken.',
  textareas: [
  {
    key: 'ethics',
    source: 'field',
    label: 'Ethical Considerations',
    hint: 'What ethical considerations will be undertaken.',
    rows: 6
  }]

},
{
  id: 'dissemination',
  label: 'Dissemination of Results',
  phase: 1,
  kind: 'text',
  summary: 'In what form the findings will be presented.',
  textareas: [
  {
    key: 'dissemination',
    source: 'field',
    label: 'Dissemination of Results / Findings',
    hint: 'In what form will your findings be presented/disseminated - e.g. report, presentation, journal article, abstract, etc?',
    rows: 4
  }]

},
{
  id: 'grant',
  label: 'Grant Request',
  phase: 2,
  kind: 'grant',
  summary: 'Whether a grant is required from SMDC, and how much.'
},
{
  id: 'principal-investigator',
  label: 'Principal Investigator',
  phase: 2,
  kind: 'principalInvestigator',
  summary: 'Name, title/position, institution, contact no. and email.'
},
{
  id: 'co-investigators',
  label: 'Co-Investigators',
  phase: 2,
  kind: 'coInvestigators',
  optional: true,
  summary: 'There is no limit to the number of co-investigators; their expertise should justify the different research areas.'
},
{
  id: 'endorsements',
  label: 'HOD & Supervisor',
  phase: 2,
  kind: 'endorsements',
  optional: true,
  summary: 'Head of Department / Institute / Unit, and supervisor.'
},
{
  id: 'documents',
  label: 'Associated Documents',
  phase: 3,
  kind: 'documents',
  summary: 'Required and related documents — PDF only, each less than 1 MB.'
},
{
  id: 'declaration',
  label: 'Declaration & Submit',
  phase: 3,
  kind: 'declaration',
  summary: 'Agree to the declaration and submit your research.'
}];


export const SUBMIT_STEP_ID = 'declaration';

export function getStep(id: string | undefined): WizardStep | undefined {
  return WIZARD_STEPS.find((step) => step.id === id);
}

export function stepIndex(id: string): number {
  return WIZARD_STEPS.findIndex((step) => step.id === id);
}

export function stepPath(id: string): string {
  return `/wizard/${id}`;
}
